import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Info } from "../../../assets/svg/info.svg";
import {
  GetOrders200Response,
  InstrumentTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { LAYER_COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { IPerpsMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";
import { useGetStatistics } from "../../../hooks/api/statistics/useGetStatistics";
import { AssetResponse } from "../../../utils/asset";
import { ButtonThemeEnum } from "../../Buttons/styles";
import RemoveOrderModal from "../../ConfirmationModal/RemoveOrderModal";
import FundingChart from "../../DepthFundingChart/FundingChart";
import SegmentedControl from "../../shared/SegmentedControl";
import DepthSection from "../DepthSection";
import TradeOrderbookSection from "../TradeOrderbookSection";
import PerpsChart from "./PerpsChart";
import {
  ChartArea,
  ChartContainer,
  ChartTab,
  ChartTabWrapper,
  ChartsContainer,
  Header,
  MarketDetailsButton,
  OrderbookTradeHistoryArea,
} from "./style";
import MarketDetailsModal from "../../MarketDetailsModal";

interface IPerpsOrderbookAndChartProps {
  onOrderbookRowClick: (
    price: string,
    size: string,
    side: SideResponse
  ) => void;
  perpInstrument?: IPerpsMarket;
  height?: string;
}

export enum ChartTypeEnum {
  PRICE = "price_chart",
  DEPTH = "depth_chart",
  FUNDING = "funding_chart",
}

function PerpsOrderbookAndChart({
  onOrderbookRowClick,
  height,
  perpInstrument,
}: IPerpsOrderbookAndChartProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "Perps.PerpsOrderbookAndChart",
  });
  const [currentChartType, setCurrentChartType] = useState<ChartTypeEnum>(
    ChartTypeEnum.PRICE
  );

  const prelaunch = perpInstrument?.pre_launch;
  const { data: statsData } = useGetStatistics(
    perpInstrument?.underlying_asset as AssetResponse,
    InstrumentTypeResponse.Perpetual
  );

  const [removingOrder, setRemovingOrder] = useState<GetOrders200Response>();
  const [showMarketDetails, setShowMarketDetails] = useState(false);

  const onCancelOrder = useCallback((order: GetOrders200Response) => {
    setRemovingOrder(order);
  }, []);

  const onClickMarketDetails = useCallback(() => {
    setShowMarketDetails(true);
  }, []);

  const controls = useMemo(() => {
    const HIGH_LOAD_MODE = process.env.REACT_APP_HIGH_LOAD_MODE === "true";

    let keys = Object.keys(ChartTypeEnum);
    if (HIGH_LOAD_MODE) keys = keys.filter((k) => k !== "FUNDING");

    const values = keys.map((k) => {
      const key = k as keyof typeof ChartTypeEnum;

      return {
        value: ChartTypeEnum[key],
        display: <ChartTab>{t(ChartTypeEnum[key])}</ChartTab>,
        textColor:
          currentChartType === ChartTypeEnum[key]
            ? TEXT_COLORS.one
            : TEXT_COLORS.three,
      };
    });
    if (prelaunch) {
      return values.slice(0, values.length - 1);
    }
    return values;
  }, [prelaunch, t, currentChartType]);

  return (
    <>
      <RemoveOrderModal
        order={removingOrder}
        onHide={() => setRemovingOrder(undefined)}
        show={!!removingOrder}
      />
      {perpInstrument && showMarketDetails && (
        <MarketDetailsModal
          show={showMarketDetails}
          onHide={() => setShowMarketDetails(false)}
          statsData={statsData}
          perpInstrument={perpInstrument}
        />
      )}
      <ChartArea style={{ height }}>
        <Header>
          <ChartTabWrapper>
            <SegmentedControl
              segments={controls}
              value={currentChartType}
              onSelect={(e) => setCurrentChartType(e as ChartTypeEnum)}
              config={{
                widthType: "fullWidth",
                backgroundColor: LAYER_COLORS.two,
                activeBackgroundColor: LAYER_COLORS.three,
                borderRadius: "10px",
                button: {
                  fontSize: FONT_SIZE.one,
                },
              }}
            />
            <MarketDetailsButton
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={onClickMarketDetails}
            >
              {t("market_details")}
              <Info />
            </MarketDetailsButton>
          </ChartTabWrapper>
        </Header>
        {perpInstrument && (
          <ChartsContainer>
            <PerpsChart
              currentChartType={currentChartType}
              perpInstrument={perpInstrument}
              onCancelOrder={onCancelOrder}
            />
            <ChartContainer hidden={currentChartType !== ChartTypeEnum.DEPTH}>
              <DepthSection perpInstrument={perpInstrument} />
            </ChartContainer>
            {currentChartType === ChartTypeEnum.FUNDING && (
              <ChartContainer
                hidden={currentChartType !== ChartTypeEnum.FUNDING}
              >
                <FundingChart instrument={perpInstrument.instrument_name} />
              </ChartContainer>
            )}
          </ChartsContainer>
        )}
      </ChartArea>
      <OrderbookTradeHistoryArea style={{ height }}>
        <TradeOrderbookSection
          perpInstrument={perpInstrument}
          onOrderbookRowClick={onOrderbookRowClick}
        />
      </OrderbookTradeHistoryArea>
    </>
  );
}

export default PerpsOrderbookAndChart;
